import { NodeApi } from 'platejs';
import z from 'zod';

import { getElementTypes } from '@@plate/utils/getElementTypes';

import { PlateTextItem, TextItemCodec } from './TextItem';
import {
    CURRENT_VERSION,
    ElementId,
    UnityElementId,
    UnityTextAttributes,
    Version,
} from '../commons';
import { ELEMENT_TYPES } from '../constants';

const BaseUnityLink = z.object({
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    text: z.string(),
    attributes: UnityTextAttributes.optional(),
});

const PlateLinkChildren = z.array(PlateTextItem);
const BasePlateLink = z.object({
    id: ElementId,
    children: PlateLinkChildren,
});

const UnityExternalLink = z.object({
    ...BaseUnityLink.shape,
    type: z.literal(ELEMENT_TYPES.EXTERNAL_LINK),
    url: z.url(),
});
type UnityExternalLink = z.infer<typeof UnityExternalLink>;

export const PlateExternalLink = z.object({
    ...BasePlateLink.shape,
    type: z.literal(ELEMENT_TYPES.EXTERNAL_LINK),
    data: z.object({
        templateElement: z.boolean().optional(),
        href: z.url(),
    }),
});
export type ExternalLinkElement = z.infer<typeof PlateExternalLink>;

const ExternalLinkCodec = z.codec(UnityExternalLink, PlateExternalLink, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.templateElement && { templateElement: value.templateElement }),
            href: value.url,
        },
        children: [TextItemCodec.decode({ ...value, type: 'text-item' })] satisfies z.infer<
            typeof PlateLinkChildren
        >,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        text: value.children.map(NodeApi.string).join(''),
        version: CURRENT_VERSION,
        attributes: value.children[0] && TextItemCodec.encode(value.children[0]).attributes,
        url: value.data.href,
        templateElement: value.data.templateElement,
    }),
});

const UnityInternalLink = z.object({
    ...BaseUnityLink.shape,
    type: z.literal(ELEMENT_TYPES.INTERNAL_LINK),
    metadataId: z.number(),
});
type UnityInternalLink = z.infer<typeof UnityInternalLink>;

export const PlateInternalLink = z.object({
    ...BasePlateLink.shape,
    type: z.literal(ELEMENT_TYPES.INTERNAL_LINK),
    data: z.object({
        templateElement: z.boolean().optional(),
        metadataId: z.number(),
    }),
});
export type InternalLinkElement = z.infer<typeof PlateInternalLink>;

const InternalLinkCodec = z.codec(UnityInternalLink, PlateInternalLink, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.templateElement && { templateElement: value.templateElement }),
            metadataId: value.metadataId,
        },
        children: [TextItemCodec.decode({ ...value, type: 'text-item' })] satisfies z.infer<
            typeof PlateLinkChildren
        >,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        text: value.children.map(NodeApi.string).join(''),
        metadataId: value.data.metadataId,
        attributes: value.children[0] && TextItemCodec.encode(value.children[0]).attributes,
        templateElement: value.data.templateElement,
        version: CURRENT_VERSION,
    }),
});

const UnityCategoryLink = z.object({
    ...BaseUnityLink.shape,
    type: z.literal(ELEMENT_TYPES.CATEGORY_LINK),
    categoryId: z.number(),
});
type UnityCategoryLink = z.infer<typeof UnityCategoryLink>;

export const PlateCategoryLink = z.object({
    ...BasePlateLink.shape,
    type: z.literal(ELEMENT_TYPES.CATEGORY_LINK),
    data: z.object({
        templateElement: z.boolean().optional(),
        categoryId: z.number(),
    }),
});
export type CategoryLinkElement = z.infer<typeof PlateCategoryLink>;

const CategoryLinkCodec = z.codec(UnityCategoryLink, PlateCategoryLink, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.templateElement && { templateElement: value.templateElement }),
            categoryId: value.categoryId,
        },
        children: [TextItemCodec.decode({ ...value, type: 'text-item' })] satisfies z.infer<
            typeof PlateLinkChildren
        >,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        text: value.children.map(NodeApi.string).join(''),
        categoryId: value.data.categoryId,
        attributes: value.children[0] && TextItemCodec.encode(value.children[0]).attributes,
        templateElement: value.data.templateElement,
        version: CURRENT_VERSION,
    }),
});

const UnityTagLink = z.object({
    ...BaseUnityLink.shape,
    type: z.literal(ELEMENT_TYPES.TAG_LINK),
    tagId: z.number(),
});
type UnityTagLink = z.infer<typeof UnityTagLink>;

export const PlateTagLink = z.object({
    ...BasePlateLink.shape,
    type: z.literal(ELEMENT_TYPES.TAG_LINK),
    data: z.object({
        templateElement: z.boolean().optional(),
        tagId: z.number(),
    }),
});
export type TagLinkElement = z.infer<typeof PlateTagLink>;

const TagLinkCodec = z.codec(UnityTagLink, PlateTagLink, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.templateElement && { templateElement: value.templateElement }),
            tagId: value.tagId,
        },
        children: [TextItemCodec.decode({ ...value, type: 'text-item' })] satisfies z.infer<
            typeof PlateLinkChildren
        >,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        text: value.children.map(NodeApi.string).join(''),
        tagId: value.data.tagId,
        attributes: value.children[0] && TextItemCodec.encode(value.children[0]).attributes,
        templateElement: value.data.templateElement,
        version: CURRENT_VERSION,
    }),
});

const UnityAuthorLink = z.object({
    ...BaseUnityLink.shape,
    type: z.literal(ELEMENT_TYPES.AUTHOR_LINK),
    authorId: z.string(),
});
type UnityAuthorLink = z.infer<typeof UnityAuthorLink>;

export const PlateAuthorLink = z.object({
    ...BasePlateLink.shape,
    type: z.literal(ELEMENT_TYPES.AUTHOR_LINK),
    data: z.object({
        templateElement: z.boolean().optional(),
        authorId: z.string(),
    }),
});
export type AuthorLinkElement = z.infer<typeof PlateAuthorLink>;

const AuthorLinkCodec = z.codec(UnityAuthorLink, PlateAuthorLink, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.templateElement && { templateElement: value.templateElement }),
            authorId: value.authorId,
        },
        children: [TextItemCodec.decode({ ...value, type: 'text-item' })] satisfies z.infer<
            typeof PlateLinkChildren
        >,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        text: value.children.map(NodeApi.string).join(''),
        authorId: value.data.authorId,
        attributes: value.children[0] && TextItemCodec.encode(value.children[0]).attributes,
        templateElement: value.data.templateElement,
        version: CURRENT_VERSION,
    }),
});

export const UnityLink = z.discriminatedUnion('type', [
    UnityExternalLink,
    UnityInternalLink,
    UnityCategoryLink,
    UnityTagLink,
    UnityAuthorLink,
]);
export type UnityLink = z.infer<typeof UnityLink>;

export const PlateLink = z.discriminatedUnion('type', [
    PlateExternalLink,
    PlateInternalLink,
    PlateCategoryLink,
    PlateTagLink,
    PlateAuthorLink,
]);
export type LinkElement = z.infer<typeof PlateLink>;

export const LINK_ELEMENT_TYPES = getElementTypes([PlateLink]);

export const LinkCodec = z.discriminatedUnion('type', [
    ExternalLinkCodec,
    InternalLinkCodec,
    CategoryLinkCodec,
    TagLinkCodec,
    AuthorLinkCodec,
]);
